import { computed, readonly, ref } from "vue";

import { windowWithEcho } from "@/helpers/websockets";
import { axiosClient } from "@/services/client";

import type {
  AccessTokenResponse,
  ActiveCallInfo,
  AnswerCallResponse,
  CallState,
  CallStateChangedEvent,
  DialerActivationEvent,
  DialerConnectionState,
  DialerType,
  GetCallStateResponse,
  RefreshCallStateResponse,
} from "./types";

/**
 * Twilio V2 Dialer API Service (Simplified).
 *
 * Browser tabs register as "online" so the API knows when a user has any
 * dialer available to receive calls. Without this, borrowers calling an
 * offline user would wait in queue indefinitely.
 *
 * - Call state via WebSocket (users.{userId} channel)
 * - Focus-based state sync
 * - Heartbeat mechanism for long calls to refresh TTL
 * - Browser online registration with heartbeat + unload safety
 */
class DialerApiService {
  // Reactive state
  private _connectionState = ref<DialerConnectionState>("disconnected");
  private _callState = ref<CallState | null>(null);
  private _isActiveDialer = ref(true); // Default: active (browser is active when alone)

  // User info
  private userId: number | null = null;

  // Echo channel subscriptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private usersChannel: any = null;
  // Heartbeat timer for refreshing call state TTL during long calls
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private static readonly HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  // Registration heartbeat to keep browser online status alive
  private registrationHeartbeatInterval: ReturnType<typeof setInterval> | null =
    null;
  private static readonly REGISTRATION_HEARTBEAT_MS = 5 * 60 * 1000; // 5 minutes

  // Bound handler reference for cleanup
  private boundUnloadHandler: (() => void) | null = null;

  // Public readonly refs
  readonly connectionState = readonly(this._connectionState);
  readonly callState = readonly(this._callState);
  /** Whether this device is the active dialer (should handle calls) */
  readonly isActiveDialer = readonly(this._isActiveDialer);

  readonly activeCall = computed<ActiveCallInfo | null>(() => {
    if (!this._callState.value) {
      return null;
    }

    const call = this._callState.value;
    return {
      sessionId: call.call_session_id?.toString() || "",
      to: call.call_data.to || "",
      from: call.call_data.from || "",
      calleeName: call.call_data.caller_name,
      businessName: call.call_data.caller_business_name,
      applicationId: call.call_data.application_id,
      direction:
        call.call_data.direction === "inbound" ? "incoming" : "outgoing",
      startTime: call.started_at ? call.started_at * 1000 : undefined,
    };
  });

  readonly isConnected = computed(
    () => this._connectionState.value === "connected"
  );

  readonly isOnCall = computed(
    () => this._callState.value?.status === "in_call"
  );

  readonly hasIncomingCall = computed(
    () => this._callState.value?.status === "ringing"
  );

  /**
   * Force-clear the local call state.
   * Used as an escape hatch when state gets stuck.
   */
  clearCallState(): void {
    this._callState.value = null;
  }

  /**
   * Connect to backend - subscribe to WebSocket channels and register browser as online.
   */
  async connect(userId: number): Promise<void> {
    if (this._connectionState.value === "connected" && this.userId === userId) {
      return;
    }

    this._connectionState.value = "connecting";
    this.userId = userId;

    try {
      // Subscribe to WebSocket channels
      await this.subscribeToChannels(userId);

      // Register browser as online so API knows this user can receive calls
      await this.registerBrowser();

      // Fetch call state after registration — this response includes the
      // authoritative is_active_dialer flag, which accounts for all currently
      // registered devices (e.g. Electron dialer already online).
      await this.fetchCallState();

      // Start heartbeat to keep registration alive (prevents TTL expiration)
      this.startRegistrationHeartbeat();

      this._connectionState.value = "connected";

      // Set up visibility change listener for focus-based sync
      this.setupFocusSync();

      // Set up beforeunload handler for reliable unregistration on tab close
      this.setupUnloadHandler();
    } catch (error) {
      console.error("[DialerApi] Connection failed:", error);
      this._connectionState.value = "error";
      throw error;
    }
  }

  /**
   * Disconnect from backend.
   */
  disconnect(): void {
    // Stop heartbeats
    this.stopHeartbeat();
    this.stopRegistrationHeartbeat();

    // Unregister browser (fire-and-forget)
    this.unregisterBrowser();

    // Fully unsubscribe from channels (Echo.leave sends pusher:unsubscribe)
    if (this.usersChannel) {
      const usersChannelName = `users.${this.userId}`;
      windowWithEcho.Echo.leave(usersChannelName);
      this.usersChannel = null;
    }
    // Remove focus listener
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );

    // Remove unload handler
    this.removeUnloadHandler();

    this.userId = null;
    this._connectionState.value = "disconnected";
    this._callState.value = null;
    this._isActiveDialer.value = true; // Reset to active (default when alone)
  }

  // ============================================
  // Call State
  // ============================================

  async fetchCallState(): Promise<void> {
    try {
      const response = await axiosClient.get<GetCallStateResponse>(
        "/api/int/twilio/call_state",
        { params: { dialer_type: "browser" } }
      );
      this._callState.value = response.data.call;
      this._isActiveDialer.value = response.data.is_active_dialer;
    } catch (error) {
      console.error("[DialerApi] Failed to fetch call state:", error);
    }
  }

  /**
   * Refresh call state TTL on the server.
   * Used for long calls to prevent state expiration.
   */
  async refreshCallState(): Promise<boolean> {
    try {
      const response = await axiosClient.post<RefreshCallStateResponse>(
        "/api/int/twilio/call_state/refresh"
      );
      return response.data.success;
    } catch (error) {
      console.error("[DialerApi] Failed to refresh call state:", error);
      return false;
    }
  }

  /**
   * Start heartbeat for long calls.
   * Automatically refreshes call state TTL every 5 minutes.
   */
  startHeartbeat(): void {
    if (this.heartbeatInterval) {
      return; // Already running
    }

    this.heartbeatInterval = setInterval(() => {
      if (this._callState.value) {
        this.refreshCallState();
      } else {
        this.stopHeartbeat();
      }
    }, DialerApiService.HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Stop heartbeat timer.
   */
  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // ============================================
  // Access Token
  // ============================================

  async getAccessToken(): Promise<AccessTokenResponse> {
    // Token identity is just user ID now - no dialer_id needed
    const response = await axiosClient.post<AccessTokenResponse>(
      "/api/int/twilio/access-token"
    );
    return response.data;
  }

  // ============================================
  // Call Commands
  // ============================================

  async answerCall(
    dialerType: DialerType = "browser"
  ): Promise<AnswerCallResponse> {
    const response = await axiosClient.post<AnswerCallResponse>(
      "/api/int/twilio/call_commands/answer",
      { from_dialer_type: dialerType }
    );
    return response.data;
  }

  async rejectCall(): Promise<AnswerCallResponse> {
    const response = await axiosClient.post<AnswerCallResponse>(
      "/api/int/twilio/call_commands/reject"
    );
    return response.data;
  }

  // ============================================
  // Backwards Compatibility Methods
  // ============================================

  acceptCall(): void {
    this.answerCall().catch(console.error);
  }

  // ============================================
  // Browser Online Registration
  // ============================================

  /**
   * Register browser as online dialer.
   * Called when connecting to backend so the API knows this user can receive calls.
   */
  async registerBrowser(): Promise<void> {
    try {
      const response = await axiosClient.post(
        "/api/int/twilio/call_state/dialer/register",
        { dialer_type: "browser" },
        { headers: { "suppress-toast": "true" } }
      );
      this._isActiveDialer.value = response.data.active ?? true;
    } catch (error) {
      console.error("[DialerApi] Failed to register browser:", error);
    }
  }

  /**
   * Unregister browser (going offline).
   * Called on disconnect or page unload.
   */
  async unregisterBrowser(): Promise<void> {
    try {
      await axiosClient.post(
        "/api/int/twilio/call_state/dialer/unregister",
        { dialer_type: "browser" },
        { headers: { "suppress-toast": "true" } }
      );
    } catch (error) {
      console.error("[DialerApi] Failed to unregister browser:", error);
    }
  }

  /**
   * Refresh browser online status (heartbeat).
   * Prevents TTL expiration during long sessions.
   */
  async refreshBrowserStatus(): Promise<void> {
    try {
      const response = await axiosClient.post(
        "/api/int/twilio/call_state/dialer/refresh",
        { dialer_type: "browser" },
        { headers: { "suppress-toast": "true" } }
      );
      // Re-sync active status in case a DialerActivationEvent was missed
      if (response.data.active !== undefined) {
        this._isActiveDialer.value = response.data.active;
      }
    } catch (error) {
      console.error("[DialerApi] Failed to refresh browser status:", error);
    }
  }

  /**
   * Start heartbeat to keep browser registration alive.
   * Prevents TTL expiration during long sessions.
   */
  private startRegistrationHeartbeat(): void {
    if (this.registrationHeartbeatInterval) {
      return; // Already running
    }

    this.registrationHeartbeatInterval = setInterval(() => {
      if (this._connectionState.value === "connected") {
        this.refreshBrowserStatus();
      }
    }, DialerApiService.REGISTRATION_HEARTBEAT_MS);
  }

  /**
   * Stop registration heartbeat.
   */
  private stopRegistrationHeartbeat(): void {
    if (this.registrationHeartbeatInterval) {
      clearInterval(this.registrationHeartbeatInterval);
      this.registrationHeartbeatInterval = null;
    }
  }

  /**
   * Set up beforeunload handler to unregister when the tab closes.
   * Uses sendBeacon for reliable delivery during page unload.
   */
  private setupUnloadHandler(): void {
    // Remove any previous handler first
    this.removeUnloadHandler();

    this.boundUnloadHandler = () => {
      // Use sendBeacon for reliable delivery during page unload.
      // sendBeacon sends cookies automatically so cookie-based auth works.
      // For JWT-only auth, the TTL expiration on the API side is the fallback.
      const baseURL = (axiosClient.defaults.baseURL || window.location.origin).replace(/\/$/, "");
      const url = `${baseURL}/api/int/twilio/call_state/dialer/unregister`;
      const data = JSON.stringify({ dialer_type: "browser" });
      navigator.sendBeacon(url, new Blob([data], { type: "application/json" }));
    };

    window.addEventListener("beforeunload", this.boundUnloadHandler);
  }

  /**
   * Remove the beforeunload handler.
   */
  private removeUnloadHandler(): void {
    if (this.boundUnloadHandler) {
      window.removeEventListener("beforeunload", this.boundUnloadHandler);
      this.boundUnloadHandler = null;
    }
  }

  // ============================================
  // WebSocket Subscriptions
  // ============================================

  private async subscribeToChannels(userId: number): Promise<void> {
    // Subscribe to users channel for call state changes
    const usersChannelName = `users.${userId}`;
    this.usersChannel = windowWithEcho.Echo.private(usersChannelName);
    this.usersChannel.listen(
      ".call_state_changed",
      (event: CallStateChangedEvent) => {
        this.handleCallStateChanged(event);
      }
    );

    // Dialer activation changes (active/inactive based on device landscape)
    this.usersChannel.listen(
      ".dialer_activation",
      (event: DialerActivationEvent) => {
        this.handleDialerActivation(event);
      }
    );

  }

  private handleCallStateChanged(event: CallStateChangedEvent): void {
    // Always track call state, even when the browser is not the active dialer.
    // This ensures watchers (e.g. useDialerConnection → App.vue) see the full
    // null → non-null → null lifecycle and can clean up dashboard UI state
    // when a call handled by the Electron app ends.
    this._callState.value = event.state;
  }

  private handleDialerActivation(event: DialerActivationEvent): void {
    // Only act on events targeted at this device type
    if (event.dialer_type !== "browser") {
      return;
    }

    this._isActiveDialer.value = event.active;
  }

  // ============================================
  // Focus-Based Sync
  // ============================================

  private handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === "visible" && this.isConnected.value) {
      await this.fetchCallState();
    }
  };

  private setupFocusSync(): void {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }
}

// Singleton export
export const dialerApi = new DialerApiService();

// Re-export types
export * from "./types";
